/**
 * MBT §6.2 — the ad module.
 *
 * ============================================================================
 * THIS SCREEN SELLS SPACE ON A GOVERNMENT SCREEN. IT SAYS SO.
 * ============================================================================
 *
 * §18 risk 5 calls commercial advertising on a hokimlik display a POLICY
 * decision, and nothing here settles it. What this screen does is make each
 * booking attributable and reversible: every slot carries the name of whoever
 * created it, stopping one is a single click, and the play counts come from
 * what the kiosks actually put on the glass rather than from what the schedule
 * intended.
 *
 * THE THREE NUMBERS the owner asked for are three separate fields, in the order
 * an operator thinks about them: how long one showing lasts, how often it may
 * repeat, and the window it runs in — dates for the campaign, clock times for
 * the day. Each is stated with its unit and its consequence, because "10" in a
 * box labelled «Takrorlanish» could mean ten minutes or ten times an hour, and
 * those differ by a factor of six on a public screen.
 *
 * What it does NOT offer is a way to make an ad interrupt anybody. There is no
 * severity here and no takeover: an ad plays on the attract screen and the
 * kiosk abandons it the moment a citizen touches anything.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  AD_DEFAULT_DURATION_SECONDS,
  AD_DEFAULT_EVERY_MINUTES,
  AD_MAX_DURATION_SECONDS,
  AD_MIN_DURATION_SECONDS,
  ANNOUNCEMENT_IMAGE_MIME,
  ANNOUNCEMENT_VIDEO_WARN_BYTES,
  ANNOUNCEMENT_VIDEO_MIME,
  KIOSK_SCREEN_HEIGHT,
  KIOSK_SCREEN_WIDTH,
  formatMinuteOfDay,
} from '@navoiy/domain';
import type { components } from '@navoiy/api-types';
import { api, ApiError, unwrap, uploadMbtMedia, type UploadedMbtMedia } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { t } from '../i18n/index.js';
import { stamp } from '../lib/format.js';
import { Button, Field, Notice, Panel, PanelHeader, Skeleton, TextInput } from '../ui/index.jsx';
import { KpiCard, PageHeader } from '../ui/console.jsx';
import { BarChart } from '../ui/charts.jsx';

type MbtAd = components['schemas']['MbtAd'];

/** «09:00» → 540. Returns null for anything that is not a clock time. */
function parseClock(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/** The local date, as an `<input type="date">` wants it. */
function isoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
}

export function AdsScreen(): React.JSX.Element {
  const { expire, allows } = useSession();
  const onExpired = useCallback(() => expire(), [expire]);
  const mayManage = allows('mbt.ads.manage');

  const [items, setItems] = useState<MbtAd[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [label, setLabel] = useState('');
  const [media, setMedia] = useState<UploadedMbtMedia | null>(null);
  const [uploading, setUploading] = useState(false);
  /** Set when the chosen file is big enough to delay its first showing. */
  const [heavy, setHeavy] = useState(false);
  const [duration, setDuration] = useState(String(AD_DEFAULT_DURATION_SECONDS));
  const [every, setEvery] = useState(String(AD_DEFAULT_EVERY_MINUTES));
  const [endsOn, setEndsOn] = useState(() => isoDate(new Date(Date.now() + 7 * 86_400_000)));
  /**
   * §6.2's per-campaign audio approval. Starts OFF every time the composer is
   * opened, and is deliberately not remembered between bookings: "muted by
   * default" is a property of each campaign, not a preference somebody sets
   * once and then stops thinking about.
   */
  const [sound, setSound] = useState(false);
  const [dailyStart, setDailyStart] = useState('08:00');
  const [dailyEnd, setDailyEnd] = useState('20:00');

  const load = useCallback(async () => {
    try {
      const data = unwrap(await api().GET('/mbt/ads', {}));
      setItems(data.items as MbtAd[]);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else if (caught instanceof ApiError && caught.status === 403) setItems([]);
      else setError(t('error.network'));
    }
  }, [onExpired]);

  useEffect(() => {
    void load();
    /**
     * A minute, not ten seconds. Unlike the broadcast board — where an operator
     * is watching kiosks confirm an evacuation notice one by one — nothing here
     * is urgent: a play count that is a minute stale changes no decision.
     */
    const timer = setInterval(() => void load(), 60_000);
    return () => clearInterval(timer);
  }, [load]);

  async function pickMedia(file: File | undefined): Promise<void> {
    if (!file) return;
    setHeavy(file.type.startsWith('video/') && file.size > ANNOUNCEMENT_VIDEO_WARN_BYTES);
    setUploading(true);
    setError(null);
    try {
      const uploaded = await uploadMbtMedia(file);
      setMedia(uploaded);
      /**
       * Default the slot to the clip's own length.
       *
       * The operator's next question after uploading is always "how long should
       * this run", and the file already answers it. Setting it here also makes
       * the mismatch visible rather than silent: a 20 s clip in a 40 s slot
       * loops, which is legitimate and surprising, and the hint below says so.
       */
      if (uploaded.duration_seconds && uploaded.duration_seconds >= AD_MIN_DURATION_SECONDS) {
        setDuration(String(Math.min(uploaded.duration_seconds, AD_MAX_DURATION_SECONDS)));
      }
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else if (caught instanceof ApiError && caught.status === 413) {
        setError(t('ads.mediaTooLarge'));
      } else if (caught instanceof ApiError && caught.status === 422) {
        // §6.2's ingest validation speaking. The file is structurally broken —
        // most often an upload or export that was cut short — and saying so
        // here is the whole point: the alternative is a booked campaign that
        // shows nothing on a screen in another building.
        setError(t('ads.mediaBroken'));
      } else setError(t('ads.mediaFailed'));
    } finally {
      setUploading(false);
    }
  }

  /*
    THE PARSED SCHEDULE, DERIVED ONCE.

    `create()` used to parse these itself. Now the live preview needs the same
    four values, and two parsers over the same four inputs is how a preview
    ends up describing a booking the server never receives — the exact class of
    bug a preview exists to prevent. So they are derived on render and both
    readers use them.

    `null` means "does not parse yet", which is a normal state while somebody
    is typing and is why the preview waits rather than showing a sentence about
    a four-second gap the moment a «4» appears.
  */
  const startMinute = parseClock(dailyStart);
  const endMinute = parseClock(dailyEnd);
  const durationSeconds = /^\d+$/.test(duration.trim()) ? Number(duration) : null;
  const everyMinutes = /^\d+$/.test(every.trim()) ? Number(every) : null;

  async function create(): Promise<void> {
    if (!label.trim()) {
      setError(t('ads.labelRequired'));
      return;
    }
    if (!media) {
      setError(t('ads.mediaRequired'));
      return;
    }
    if (startMinute === null || endMinute === null || endMinute <= startMinute) {
      setError(t('ads.dailyWindowInvalid'));
      return;
    }
    /**
     * The campaign ends at the END of the chosen day, not at its midnight
     * start. «Ends 24 August» means the 24th is included — reading it the other
     * way silently loses the last day of every booking, and the person who
     * notices is the one who paid for it.
     */
    const endsAt = new Date(`${endsOn}T23:59:59`);
    if (Number.isNaN(endsAt.getTime()) || endsAt.getTime() <= Date.now()) {
      setError(t('ads.endsAtInvalid'));
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await api().POST('/mbt/ads', {
        body: {
          label: label.trim(),
          media_id: media.media_id,
          duration_seconds: Number(duration),
          every_minutes: Number(every),
          sound,
          ends_at: endsAt.toISOString(),
          daily_start_minute: startMinute,
          daily_end_minute: endMinute,
        },
      });
      setLabel('');
      setMedia(null);
      setSound(false);
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else if (caught instanceof ApiError && caught.status === 403) {
        setError(t('ads.noPermission'));
      } else setError(t('ads.createFailed'));
    } finally {
      setBusy(false);
    }
  }

  /** §6.2's approval, changed on a booking that already exists. */
  async function toggleSound(ad: MbtAd): Promise<void> {
    setBusy(true);
    try {
      await api().POST('/mbt/ads/{ad_id}/sound', {
        params: { path: { ad_id: ad.id } },
        body: { enabled: !ad.sound },
      });
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else setError(t('ads.soundFailed'));
    } finally {
      setBusy(false);
    }
  }

  /** Stop a slot, or start a stopped one running again. */
  async function setPaused(ad: MbtAd, paused: boolean): Promise<void> {
    setBusy(true);
    try {
      if (paused) {
        await api().POST('/mbt/ads/{ad_id}/pause', { params: { path: { ad_id: ad.id } } });
      } else {
        await api().POST('/mbt/ads/{ad_id}/resume', { params: { path: { ad_id: ad.id } } });
      }
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else setError(t('ads.pauseFailed'));
    } finally {
      setBusy(false);
    }
  }

  const live = (items ?? []).filter((ad) => ad.live && !ad.paused_at);
  const playsTotal = (items ?? []).reduce((sum, ad) => sum + (ad.plays?.total ?? 0), 0);
  const cutTotal = (items ?? []).reduce((sum, ad) => sum + (ad.plays?.cut_short ?? 0), 0);

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow={<span>{t('ads.eyebrow')}</span>}
        title={t('nav.ads')}
        subtitle={t('ads.subtitle')}
        right={
          <p className="text-hero font-bold tabular-nums leading-none text-ink-100">
            {live.length}
          </p>
        }
      />

      {error ? <Notice tone="warn">{error}</Notice> : null}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label={t('ads.kpiLive')}
          value={String(live.length)}
          absent={t('app.notYet')}
          tone={live.length > 0 ? 'ok' : 'neutral'}
          footerLabel={t('ads.kpiBooked')}
          footerValue={String(items?.length ?? 0)}
        />
        <KpiCard
          label={t('ads.kpiPlays')}
          value={String(playsTotal)}
          absent={t('app.notYet')}
          spread={(items ?? []).map((ad) => ad.plays?.total ?? 0)}
          tone="ok"
          footerLabel={t('ads.kpiCut')}
          footerValue={String(cutTotal)}
        />
        <KpiCard
          label={t('ads.kpiCompletion')}
          /*
            The honest completion rate: full plays over all plays.
            §6.2's cut-short count exists so this figure cannot be inflated —
            an invoice built on «total plays» alone would bill for every slot a
            citizen walked into and ended.
          */
          value={playsTotal === 0 ? null : `${Math.round(((playsTotal - cutTotal) / playsTotal) * 100)}%`}
          absent={t('app.notYet')}
          tone="neutral"
          footerLabel={t('ads.kpiCutMeaning')}
          footerValue=""
        />
        <KpiCard
          label={t('ads.kpiSound')}
          value={String((items ?? []).filter((ad) => ad.sound).length)}
          absent={t('app.notYet')}
          tone={(items ?? []).some((ad) => ad.sound && ad.live) ? 'warn' : 'neutral'}
          footerLabel={t('ads.kpiPaused')}
          footerValue={String((items ?? []).filter((ad) => ad.paused_at).length)}
        />
      </div>

      {(items ?? []).length > 0 ? (
        <Panel>
          <PanelHeader title={t('ads.playsChart')} aside={t('ads.playsChartHint')} />
          <div className="px-4 py-4">
            <BarChart
              bars={(items ?? [])
                .filter((ad) => (ad.plays?.total ?? 0) > 0)
                .slice(0, 12)
                .map((ad) => ({
                  key: ad.id,
                  /* NOT sliced. `BarChart` truncates to the column and puts the
                     full label in a `title`, so hovering recovers which of two
                     «Xivada Pre…» campaigns a bar belongs to. Slicing here threw
                     that away before the component ever saw it. */
                  label: ad.label,
                  value: ad.plays?.total ?? 0,
                  highlight: (ad.plays?.cut_short ?? 0) > 0,
                }))}
            />
          </div>
        </Panel>
      ) : null}

      {mayManage ? (
        <Panel>
          <PanelHeader title={t('ads.composeTitle')} aside={t('ads.composeHint')} />
          {/*
            ====================================================================
            TWO COLUMNS: THE FORM, AND WHAT IT WILL LOOK LIKE.
            ====================================================================

            Booking an ad used to be a single stack of eight fields with the
            preview buried in the middle of it, and the operator found out what
            they had actually booked by pressing «Yaratish» and reading the row
            that appeared. Everything on the right of this layout exists so that
            does not happen: the media at the kiosk's real aspect, and the whole
            schedule restated as ONE SENTENCE that updates as they type.

            The sentence is the same `ads.scheduleSummary` string the list uses,
            deliberately — what you read before booking is word for word what
            you read afterwards, so there is nothing to translate between the
            two and no way for them to drift apart.
          */}
          <div className="grid gap-6 px-4 py-4 lg:grid-cols-[1fr_20rem]">
            <div className="space-y-4">
              {/* §18 risk 5, said on the screen rather than buried in a
                  document. The operator booking a slot is the person who
                  should read it. */}
              <Notice tone="quiet">{t('ads.policyNote')}</Notice>

              <p className="text-nano font-semibold uppercase text-ink-500">{t('ads.stepWhat')}</p>

              <Field label={t('ads.label')} hint={t('ads.labelHint')}>
                <TextInput value={label} onChange={(event) => setLabel(event.target.value)} />
              </Field>

              <Field
                label={t('ads.media')}
                hint={t('ads.mediaHint', {
                  width: String(KIOSK_SCREEN_WIDTH),
                  height: String(KIOSK_SCREEN_HEIGHT),
                })}
              >
                <input
                  type="file"
                  accept={[...ANNOUNCEMENT_IMAGE_MIME, ...ANNOUNCEMENT_VIDEO_MIME].join(',')}
                  className="block w-full text-label text-ink-300 file:mr-3 file:rounded file:border-0 file:bg-panel-raised file:px-3 file:py-1.5 file:text-label file:text-ink-100"
                  onChange={(event) => void pickMedia(event.target.files?.[0])}
                />
                {uploading ? (
                  <p className="mt-2 text-micro text-ink-500">{t('ads.mediaUploading')}</p>
                ) : null}
                {heavy ? (
                  <p className="mt-2 text-micro text-warn">{t('ads.mediaHeavy')}</p>
                ) : null}
              </Field>

              <p className="pt-2 text-nano font-semibold uppercase text-ink-500">
                {t('ads.stepHow')}
              </p>

            {/* The three numbers. Each hint states the CONSEQUENCE, not the
                unit again: an operator who reads "10" needs to know whether a
                citizen waiting to use the kiosk waits behind it. */}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('ads.duration')} hint={t('ads.durationHint')}>
                <TextInput
                  inputMode="numeric"
                  value={duration}
                  onChange={(event) => setDuration(event.target.value)}
                />
                {/* The clip's length against the slot's. Neither case is an
                    error — a short clip loops and a long one is cut — but both
                    surprise an operator who was not told. */}
                {media?.duration_seconds ? (
                  Number(duration) > media.duration_seconds ? (
                    <p className="mt-2 text-micro text-ink-500">
                      {t('ads.durationLoops', {
                        video: String(media.duration_seconds),
                        slot: duration,
                      })}
                    </p>
                  ) : Number(duration) < media.duration_seconds ? (
                    <p className="mt-2 text-micro text-warn">
                      {t('ads.durationCuts', {
                        video: String(media.duration_seconds),
                        slot: duration,
                      })}
                    </p>
                  ) : null
                ) : null}
              </Field>
              <Field label={t('ads.every')} hint={t('ads.everyHint')}>
                <TextInput
                  inputMode="numeric"
                  value={every}
                  onChange={(event) => setEvery(event.target.value)}
                />
              </Field>
            </div>

            {/*
              §6.2: "Audio in ads is muted by default. A kiosk in a mahalla
              building should not shout. Sound requires explicit per-campaign
              approval." This checkbox IS that approval, and the hint says what
              turning it on means in the room rather than what it does to the
              player.
            */}
            <Field label={t('ads.sound')} hint={t('ads.soundHint')}>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={sound}
                  onChange={(event) => setSound(event.target.checked)}
                  className="h-4 w-4"
                />
                <span className="text-label text-ink-100">{t('ads.soundOn')}</span>
              </label>
            </Field>

              <p className="pt-2 text-nano font-semibold uppercase text-ink-500">
                {t('ads.stepWhen')}
              </p>

            <div className="grid gap-4 sm:grid-cols-3">
              <Field label={t('ads.endsOn')} hint={t('ads.endsOnHint')}>
                <TextInput
                  type="date"
                  value={endsOn}
                  onChange={(event) => setEndsOn(event.target.value)}
                />
              </Field>
              <Field label={t('ads.dailyStart')}>
                <TextInput
                  type="time"
                  value={dailyStart}
                  onChange={(event) => setDailyStart(event.target.value)}
                />
              </Field>
              <Field label={t('ads.dailyEnd')} hint={t('ads.dailyHint')}>
                <TextInput
                  type="time"
                  value={dailyEnd}
                  onChange={(event) => setDailyEnd(event.target.value)}
                />
              </Field>
            </div>

            <Button onClick={() => void create()} disabled={busy || uploading}>
              {t('ads.create')}
            </Button>
            </div>

            {/* ---------------------------------------------------------- */}
            {/* THE PREVIEW COLUMN                                          */}
            {/* ---------------------------------------------------------- */}
            <div className="space-y-3">
              <p className="text-nano font-semibold uppercase text-ink-500">
                {t('ads.previewTitle')}
              </p>

              {/* The kiosk's own aspect, always — even empty. An operator who
                  has not chosen a file yet should still see the shape their
                  media has to fill, which is the whole reason the recommended
                  size is printed on this screen. */}
              <div className="relative aspect-[9/16] w-full overflow-hidden rounded-card border border-hairline bg-canvas">
                {media === null ? (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-1 px-4 text-center">
                    <span className="text-micro text-ink-500">{t('ads.previewEmpty')}</span>
                    <span className="text-nano text-ink-500">
                      {KIOSK_SCREEN_WIDTH}×{KIOSK_SCREEN_HEIGHT}
                    </span>
                  </div>
                ) : media.kind === 'image' ? (
                  <img src={media.url} alt="" className="h-full w-full object-cover" />
                ) : (
                  /* The chosen clip PLAYS here, unlike the cards in the list.
                     There is one of it rather than twelve, and an operator
                     about to book a minute of public screen time should watch
                     the thing once before they do. */
                  <video
                    src={media.url}
                    className="h-full w-full object-cover"
                    muted
                    loop
                    autoPlay
                    playsInline
                  />
                )}
              </div>

              {media === null ? null : (
                <Button tone="quiet" onClick={() => setMedia(null)}>
                  {t('ads.mediaRemove')}
                </Button>
              )}

              {/*
                THE BOOKING, IN ONE SENTENCE, BEFORE IT EXISTS.

                Three numbers in three boxes is how a booking gets misread — the
                owner's own «10» could be ten minutes or ten times an hour. This
                reads the fields back as prose, so «40 soniya · har 1 daqiqada»
                is visible as the near-continuous loop it actually is while
                there is still time to change it.

                Only rendered once the numbers parse: a half-typed «4» would
                otherwise flash a sentence about a four-second gap.
              */}
              {durationSeconds !== null && everyMinutes !== null && startMinute !== null && endMinute !== null ? (
                <div className="rounded-card border border-hairline bg-panel-raised px-3 py-2">
                  <p className="text-micro leading-relaxed text-ink-300">
                    {t('ads.scheduleSummary', {
                      seconds: String(durationSeconds),
                      minutes: String(everyMinutes),
                      from: formatMinuteOfDay(startMinute),
                      to: formatMinuteOfDay(endMinute),
                      until: endsOn === '' ? t('app.unknown') : endsOn,
                    })}
                    {sound ? ` · ${t('ads.soundTag')}` : ''}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </Panel>
      ) : null}

      <Panel>
        <PanelHeader title={t('ads.listTitle')} aside={t('ads.listHint')} />
        <div className="px-4 py-4">
          {items === null ? (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {[0, 1, 2].map((key) => (
                <Skeleton key={key} className="h-72" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <p className="py-6 text-center text-label text-ink-500">{t('ads.empty')}</p>
          ) : (
            /*
              CARDS, NOT ROWS.

              The list used to be full-width rows: a 12×20 grey box labelled
              «Video», then a two-line title running the whole width of the
              screen, then two runs of dot-separated metadata that wrapped
              wherever they happened to. Nothing lined up between one booking
              and the next, so comparing two campaigns meant reading two
              paragraphs.

              A card fixes that by giving every field a fixed place: the media
              is always top, the schedule is always four chips in the same
              order, the plays bar is always at the same height. Three across,
              so a dozen bookings are one screen and the eye can scan a column
              of durations rather than hunting for the word «soniya».
            */
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {items.map((ad) => (
                <AdCard
                  key={ad.id}
                  ad={ad}
                  mayManage={mayManage}
                  busy={busy}
                  onToggleSound={() => void toggleSound(ad)}
                  onTogglePause={() => void setPaused(ad, !ad.paused_at)}
                />
              ))}
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}

/**
 * The media preview, at the kiosk's own aspect.
 *
 * ============================================================================
 * AN IMAGE GETS ITS PICTURE. A VIDEO GETS A DESIGNED PLATE, NOT A FRAME.
 * ============================================================================
 *
 * The obvious thing — a `<video preload="metadata">` seeked half a second in —
 * was tried and does not work here, for a reason worth writing down so nobody
 * tries it again:
 *
 * These clips are NOT faststart MP4s. Their first boxes are `ftyp` then
 * straight into `mdat`, which puts the `moov` index at the END of the file. A
 * browser therefore cannot know the frame layout until it has read to the tail
 * of a 25–54 MB object, and fourteen cards asking for that at once left every
 * thumbnail in NETWORK_LOADING indefinitely. (Setting `Content-Length` on the
 * media route was a real bug and is fixed, but it only makes the tail
 * REACHABLE — it does not make fourteen large reads a good idea on a shared
 * VPS.) Making the frame cheap would mean rewriting the uploads with ffmpeg,
 * which this project has deliberately never taken on.
 *
 * So the video plate is DESIGNED rather than photographic: the console's own
 * navy-and-gold bloom, a play mark, and the clip's real length. It is a
 * recognisable object at a glance, which is what the grey box labelled «Video»
 * never was — and it costs one HTTP request less than nothing.
 *
 * The composer still autoplays the single clip an operator just uploaded.
 * That is one video, chosen deliberately, and worth the wait.
 */
function AdMedia({ ad }: { readonly ad: MbtAd }) {
  if (ad.media?.kind === 'image') {
    return <img src={ad.media.url} alt="" className="h-full w-full object-cover" />;
  }
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-brand-deep bg-hero">
      {/* A play mark, drawn rather than imported: one triangle needs no icon
          set, and this is the only place in the app that wants one. */}
      <svg viewBox="0 0 24 24" className="h-10 w-10 text-white/80" aria-hidden="true">
        <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" strokeWidth="1.2" />
        <path d="M10 8.5l6 3.5-6 3.5z" fill="currentColor" />
      </svg>
      <span className="text-nano uppercase text-white/60">{t('ads.mediaKind.video')}</span>
      {ad.media?.duration_seconds ? (
        <span className="text-label font-semibold tabular-nums text-white">
          {t('ads.seconds', { n: String(ad.media.duration_seconds) })}
        </span>
      ) : null}
    </div>
  );
}

/** One labelled fact. Four of these replace a run of dot-separated text. */
function AdStat({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="min-w-0">
      <p className="truncate text-nano uppercase text-ink-500">{label}</p>
      <p className="truncate text-label font-semibold tabular-nums text-ink-100">{value}</p>
    </div>
  );
}

function AdCard({
  ad,
  mayManage,
  busy,
  onToggleSound,
  onTogglePause,
}: {
  readonly ad: MbtAd;
  readonly mayManage: boolean;
  readonly busy: boolean;
  readonly onToggleSound: () => void;
  readonly onTogglePause: () => void;
}) {
  const total = ad.plays?.total ?? 0;
  const cut = ad.plays?.cut_short ?? 0;
  const completed = total - cut;
  /* The share that ran to the end. §6.2's cut-short count exists so a booking
     cannot be billed on «plays» alone, so it is the bar and not a footnote. */
  const pct = total === 0 ? 0 : Math.round((completed / total) * 100);

  const state = ad.paused_at
    ? { label: t('ads.paused'), className: 'bg-canvas/85 text-ink-500' }
    : ad.live
      ? { label: t('ads.live'), className: 'bg-ok/90 text-white' }
      : { label: t('ads.scheduled'), className: 'bg-canvas/85 text-ink-300' };

  return (
    <div className="flex flex-col overflow-hidden rounded-card border border-hairline bg-panel shadow-card transition-shadow duration-state hover:shadow-lift">
      {/* 9:16 — an ad fills a kiosk screen, and a kiosk is a panel on its end.
          A 16:9 thumbnail would show a letterboxed sliver of the real thing. */}
      <div className="relative aspect-[9/16] max-h-64 w-full bg-canvas">
        <AdMedia ad={ad} />
        <span
          className={`absolute left-2 top-2 rounded px-1.5 py-0.5 text-nano font-semibold uppercase ${state.className}`}
        >
          {state.label}
        </span>
        {ad.sound ? (
          /* §6.2 makes sound an explicit per-campaign approval, so it is a
             visible property of the booking and not a checkbox somebody has to
             open the row to find. */
          <span className="absolute right-2 top-2 rounded bg-warn/90 px-1.5 py-0.5 text-nano font-semibold uppercase text-white">
            {t('ads.soundTag')}
          </span>
        ) : null}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-3 p-3">
        {/* Two lines, then an ellipsis. These labels are whole sentences an
            operator pasted from a press release; letting one run to six lines
            pushed every other field out of alignment with the next card. */}
        <p className="line-clamp-2 text-label font-semibold leading-snug text-ink-100" title={ad.label}>
          {ad.label}
        </p>

        {/* THE SCHEDULE, as four fields in a fixed grid. Same order in every
            card, so a column of them can be compared without reading. */}
        <div className="grid grid-cols-2 gap-x-3 gap-y-2">
          <AdStat label={t('ads.statDuration')} value={t('ads.seconds', { n: String(ad.duration_seconds) })} />
          <AdStat label={t('ads.statEvery')} value={t('ads.minutes', { n: String(ad.every_minutes) })} />
          <AdStat
            label={t('ads.statWindow')}
            value={`${formatMinuteOfDay(ad.daily_start_minute)}–${formatMinuteOfDay(ad.daily_end_minute)}`}
          />
          <AdStat label={t('ads.statUntil')} value={stamp(new Date(ad.ends_at))} />
        </div>

        <div className="mt-auto space-y-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-nano uppercase text-ink-500">{t('ads.statPlays')}</span>
            <span className="text-label font-semibold tabular-nums text-ink-100">{total}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-hairline">
            <div
              className="h-full rounded-full bg-ok"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-nano text-ink-500">
            {total === 0
              ? t('ads.playsNone')
              : t('ads.playsBreakdown', { pct: String(pct), cut: String(cut) })}
          </p>
        </div>

        <p className="truncate text-nano text-ink-500" title={ad.created_by_name}>
          {t('ads.createdBy', { name: ad.created_by_name })}
        </p>

        {mayManage ? (
          <div className="flex gap-2 border-t border-hairline pt-3">
            <Button tone="quiet" onClick={onToggleSound} disabled={busy}>
              {ad.sound ? t('ads.soundOff') : t('ads.soundOnAction')}
            </Button>
            <Button tone={ad.paused_at ? 'primary' : 'quiet'} onClick={onTogglePause} disabled={busy}>
              {ad.paused_at ? t('ads.resume') : t('ads.pause')}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
